import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { carTestLabels } from "@/i18n";

const LEAK_SOURCES = [
  "Motor",
  "Växellåda",
  "Bromsar",
  "Kylare",
  "Annat",
] as const;

interface LeaksSectionProps {
  leaks: { problem?: string; source?: string; other?: string } | null;
  onLeaksChange: (leaks: {
    problem?: string;
    source?: string;
    other?: string;
  }) => void;
  onNext: () => void;
}

export const LeaksSection = ({
  leaks,
  onLeaksChange,
  onNext,
}: LeaksSectionProps) => {
  const [otherText, setOtherText] = useState(leaks?.other || "");
  const { language } = useLanguage();
  const ct = carTestLabels(language);
  const form = ct.form ?? {};
  const v = ct.pdf?.values ?? ({} as Record<string, string>);
  const opt =
    (v as { optionValues?: Record<string, string> }).optionValues ?? {};

  const handleResultChange = (problem: string) => {
    if (problem === "no") {
      onLeaksChange({ problem });
    } else {
      onLeaksChange({ problem, source: undefined, other: undefined });
    }
  };

  const handleSourceChange = (source: string) => {
    if (source === "Annat") {
      onLeaksChange({ ...leaks, source, other: otherText });
    } else {
      onLeaksChange({ ...leaks, source, other: undefined });
    }
  };

  const handleOtherTextChange = (text: string) => {
    setOtherText(text);
    if (leaks?.source === "Annat") {
      onLeaksChange({ ...leaks, other: text });
    }
  };

  const showNextButton =
    leaks?.problem === "yes" && leaks?.source === "Annat" && otherText.trim();

  return (
    <div className="space-y-4">
      <div>
        <h4 className="font-medium text-sm mb-3">{form.questionLeaks}</h4>
        <div className="grid grid-cols-2 gap-1.5 mb-4">
          <Button
            onClick={() => handleResultChange("yes")}
            variant="formOption"
            className={`${
              leaks?.problem === "yes"
                ? "border-accent border-2"
                : "border-gray-400"
            }`}
          >
            {v.yes}
          </Button>
          <Button
            onClick={() => handleResultChange("no")}
            variant="formOption"
            className={`${
              leaks?.problem === "no"
                ? "border-accent border-2"
                : "border-gray-400"
            }`}
          >
            {v.no}
          </Button>
        </div>

        {leaks?.problem === "yes" && (
          <div className="space-y-3">
            <h5 className="font-medium text-sm">{form.questionSource}</h5>
            <div className="grid grid-cols-2 gap-1.5">
              {LEAK_SOURCES.map((source) => (
                <Button
                  key={source}
                  onClick={() => handleSourceChange(source)}
                  variant="formOption"
                  className={`${
                    leaks?.source === source
                      ? "border-accent border-2"
                      : "border-gray-400"
                  }`}
                >
                  {opt[source] ?? source}
                </Button>
              ))}
            </div>

            {leaks?.source === "Annat" && (
              <div className="space-y-2">
                <Input
                  type="text"
                  placeholder={form.placeholderDescribeProblem}
                  value={otherText}
                  onChange={(e) => handleOtherTextChange(e.target.value)}
                  className="w-full"
                />
              </div>
            )}
          </div>
        )}

        {showNextButton && (
          <Button className="w-full mt-4" onClick={onNext}>
            {form.next}
          </Button>
        )}
      </div>
    </div>
  );
};
